// Enhanced types for the word editing system
export interface BoundingBox {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface WordData {
  wordId: number;
  text: string;
  boundingBox: BoundingBox;
  confidence: number;
  run_id: number;
}

export interface BlockData {
  blockId: number;
  blockType: string;
  boundingBox: BoundingBox;
  words: WordData[];
  readBlock?: boolean;
  blockReadOrder?: number;
  // Enhanced JSON specific fields
  modifiedWords?: string | null;
  mergedFromBlocks?: number[] | null;
  review?: string;
  possibleIssue?: string;
  'ai-confidence'?: number;
}

// Enhanced JSON structure interfaces
export interface EnhancedWordData {
  wordId: number;
  text: string;
  boundingBox: BoundingBox;
  confidence: number;
}

export interface EnhancedBlockData {
  blockId: number;
  blockType: string;
  readBlock: boolean;
  words: EnhancedWordData[];
  boundingBox: BoundingBox;
  blockReadOrder: number;
  modifiedWords?: string | null;
  mergedFromBlocks?: number[] | null;
  review: string;
  possibleIssue: string;
  'ai-confidence': number;
}

export interface EnhancedPageData {
  pageType: string;
  review: string;
  possibleIssue: string;
  'ai-confidence': number;
  voiceAttributes: string;
}

export interface EnhancedPageWordsData {
  library: string;
  timestamp: string;
  image_filename: string;
  image_dimensions: BoundingBox;
  total_blocks: number;
  total_words: number;
  page: EnhancedPageData;
  blocks: EnhancedBlockData[];
}

// Legacy interface - keeping for backward compatibility during transition
export interface PageWordsData {
  library: string;
  timestamp: string;
  image_filename: string;
  image_dimensions: BoundingBox;
  processed_responses: Array<{
    run: {
      blocks: BlockData[];
    };
  }>;
}

export interface PageData {
  'page-index': number;
  filename: string;
  original?: string;
  text_extracted?: string | boolean;
  words?: string;
  'words-enhanced'?: string;
  width: number;
  height: number;
  pageType?: string;
}

export interface BookSpineData {
  book: {
    isbn: string;
    fast_track: boolean;
  };
  pages: PageData[];
  copyright: PageData[];
}

// Editing modes
export type EditingMode = 'view' | 'edit' | 'word-edit' | 'block-edit' | 'page-edit';

// Storage types
export type StorageType = 'local' | 'aws';

// Edit operations for undo/redo
export interface EditOperation {
  id: string;
  type: 'word-edit' | 'word-create' | 'word-delete' | 'block-edit' | 'block-create' | 'block-delete' | 'page-edit';
  pageIndex: number;
  data: any;
  timestamp: number;
}

// Configuration for page and block types
export const PAGE_TYPES = [
  'about-author',
  'abstract',
  'abbreviations',
  'acknowledgments',
  'advertisement',
  'appendix',
  'back-cover',
  'back-page',
  'bibliography',
  'blank-page',
  'character-list',
  'chronology',
  'content',
  'copyright',
  'cover',
  'dedication',
  'epigraph',
  'foreword',
  'genealogy',
  'glossary',
  'index',
  'maps',
  'multi',
  'no-text',
  'notes',
  'other-non-content',
  'preface',
  'pronunciation',
  'table-of-contents',
  'title'
] as const;

export const BLOCK_TYPES = [
  '',
  'book-name',
  'chapter',
  'title',
  'content',
  'character-name',
  'image-caption',
  'image-description',
  'information-sign',
  'object-text',
  'q-and-a-answer',
  'q-and-a-question',
  'sound-effect',
  'speech-bubble',
  'page-number'
] as const;

export type PageType = typeof PAGE_TYPES[number] | '';
export type BlockType = typeof BLOCK_TYPES[number];

// Enhanced JSON constants
export const REVIEW_STATUSES = [
  'needs-review',
  'done',
  'in-progress'
] as const;

export type ReviewStatus = typeof REVIEW_STATUSES[number];

export const ISSUE_TYPES = [
  'block-merged',
  'ocr-accuracy-concerns',
  'text-overlap',
  'missing-text',
  'incorrect-text',
  'layout-issues',
  'confidence-low'
] as const;

export type IssueType = typeof ISSUE_TYPES[number];

// Enhanced editing interfaces
export interface PageEditData {
  pageType: PageType;
  pageIndex: number;
  isbn: string;
}

export interface BlockEditData {
  blockId: number;
  blockType: BlockType;
  blockReadOrder?: number;
  readBlock?: boolean;
  aiConfidence?: number;
  possibleIssue?: string;
}

export interface WordEditData {
  wordId: number;
  text: string;
  boundingBox: BoundingBox;
  confidence: number;
}

// Editing state interfaces
export interface EditingState {
  mode: EditingMode;
  selectedPage?: PageEditData;
  selectedBlock?: BlockEditData;
  selectedWord?: WordEditData;
  isPreviewMode: boolean;
  hasUnsavedChanges: boolean;
}

// Edit operation types
export type EditOperationType = 
  | 'page-edit' 
  | 'block-edit' 
  | 'word-edit' 
  | 'word-create' 
  | 'word-delete' 
  | 'block-create' 
  | 'block-delete';